import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AddIcon,
  ArrowIcon,
  CheckBoxActiveIcon,
  CheckBoxInActiveIcon,
  DeleteIcon,
  EditIcon,
  NotInLocationIcon,
  RedirectIcon
} from "../../Assets/assets";
import { isValidArray, isValidObject } from "../../Services/validators";
import {
  alignPhoneNumber,
  capitalizeFirstLetter,
  checkArraysAreEqual,
  dateAndTimeConverter,
  getInitials,
  isValidPhoneNumber,
  openInNewTab
} from "../../Utils/Constants/Constants";
import { hours } from "../../Utils/Constants/Procedures";
import {
  capturesFilterData,
  defaultCapturesFilter
} from "../../Utils/Constants/Workforce";
import Button from "../Button/Button";
import { CaptureItem, SelectionRadioCard } from "../Cards/Cards";
import CropperModal from "../CropperModal/CropperModal";
import CustomSelect from "../CustomSelect/CustomSelect";
import DateRange from "../Filters/DateRange";
import Modal from "../Modal/Modal";
import PreviewBar from "../PreviewBar/PreviewBar";
import InputBox from "../InputBox/InputBox";

function WorkforcePreview(props) {
  const [step, setStep] = useState("captures");
  const [file, setFile] = useState({ image: null });
  const [croppedImage, setCroppedImage] = useState(null);
  const [isFaceDetected, setIsFaceDetected] = useState(null);
  const [manualApprovalData, setManualApprovalData] = useState(null);
  const [formData, setFormData] = useState({
    type: "",
    name: "",
    phoneNumber: "+91",
    email: "",
    roles: [],
    locations: [],
    linkedProcedures: []
  });
  const [isEditing, setIsEditing] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState(defaultCapturesFilter);
  const [currentPage, setCurrentPage] = useState(1);
  const [updateReference, setUpdateReference] = useState(false);
  const [hasChanges, setHasChanges] = useState({
    basic: false,
    location: false,
    procedure: false
  });
  const [
    showDeletePictureConfirmationModal,
    setShowDeletePictureConfirmationModal
  ] = useState(false);
  const [
    showArchiveConfirmationModal,
    setShowArchiveConfirmationModal
  ] = useState(false);
  const [
    showUnarchiveConfirmationModal,
    setShowUnarchiveConfirmationModal
  ] = useState(false);
  const isArchived = !!props.data?.actions?.deletedAt;

  useEffect(() => {
    if (isValidArray(props.capturePictureData)) {
      props.getSelectedEmployeeCaptureAssets(
        currentPage,
        props.data?.documentId
      );
    }
    // eslint-disable-next-line
  }, [currentPage]);

  const isValidNewPhoneNumber = () => {
    function isPhoneNumberExists(phoneNumber) {
      if (props.workforceData) {
        return Object.values(props.workforceData).some(
          (data) => data.phoneNumber === phoneNumber
        );
      } else {
        return false;
      }
    }

    if(props.data.phoneNumber === formData.phoneNumber){
      return {
        status: true
      }
    }

    if(formData.phoneNumber?.length !== 13 || !isValidPhoneNumber(formData.phoneNumber)){
      return {
        status: false,
        message: "Please enter valid phone number"
      }
    } else if (isPhoneNumberExists(formData.phoneNumber)){
      return {
        status: false,
        message: "Phone number already exists"
      }
    } else {
      return {
        status: true
      }
    }
  }

  const handleDisable = useMemo(() => {
    const hasAnyChanges = Object.values(hasChanges).some(Boolean);

    if (!hasAnyChanges) return true;
    if (croppedImage && !isFaceDetected) return true;

    if (
      !formData.name ||
      !formData.name.length > 0 ||
      (props.data?.type === "employee" &&
        !isValidNewPhoneNumber()?.status) ||
      props.data?.type === "admin"
    ) {
      return true;
    }

    return false;
  // eslint-disable-next-line
  }, [
    hasChanges,
    croppedImage,
    isFaceDetected,
    formData.name,
    formData.phoneNumber,
    props.data?.type,
    props.workforceData
  ]);

  useEffect(() => {
    if (isValidObject(props.filterData)) {
      setSelectedFilters(props.filterData);
    }
  }, [props.filterData]);

  useEffect(() => {
    if (!props.data) return;
    setFormData((prev) => ({
      ...prev,
      type: props.data.type || "",
      name: props.data.name || "",
      phoneNumber: props.data.phoneNumber || "+91",
      email: props.data.email || "",
      roles: Array.isArray(props.data.roles)
        ? [...props.data.roles].sort((a, b) => a - b)
        : [],
      locations: props.data?.connected?.location || [],
      linkedProcedures: props.data?.linkedProcedures
    }));
  }, [props.data, step]);

  useEffect(() => {
    const documentId = props.data?.documentId;
    if (documentId) {
      props.getSelectedEmployeeCapture(documentId);
      props.getReferencePictureAsset(documentId);
    }

    if (croppedImage) {
      setCroppedImage(null);
    }
    // eslint-disable-next-line
  }, [props.data?.documentId, props.filterData]);

  const handleUploadEmployeeData = () => {
    if (hasChanges.basic) {
      props.updateEmployeeData({
        documentId: props.data?.documentId,
        ...(formData.name !== props.data?.name ? { name: formData.name } : {}),
        ...(formData.phoneNumber !== props.data?.phoneNumber
          ? { phoneNumber: formData.phoneNumber }
          : {}),
        ...(formData.email !== props.data?.email
          ? { email: formData.email }
          : {}),
        ...(!checkArraysAreEqual(formData?.roles, props.data?.roles)
          ? { roles: formData.roles }
          : {}),
        ...(croppedImage ? { file: croppedImage } : {}),
        ...(croppedImage && props.referencePictureData?.documentId
          ? {
              referencePictureDocumentId: props.referencePictureData?.documentId
            }
          : {})
      });
      setCroppedImage(null);
    }

    if (hasChanges.location) {
      props.assignLocations(props.data.documentId, formData.locations);
    }

    if (hasChanges.procedure) {
      props.updateProcedures(props.data.documentId, formData.linkedProcedures);
    }
  };

  const getImageSource = () => {
    if (croppedImage) {
      return URL.createObjectURL(croppedImage);
    } else if (props.referencePictureData?.fileSrc) {
      return props.referencePictureData.fileSrc;
    } else {
      return null;
    }
  };

  const getValidRoles = () => {
    if (isValidObject(props.roles)) {
      const roles = { ...props.roles };
      return Object.values(roles)
        .filter((role) => isValidObject(role.attendance?.capture))
        .map((role) => role.documentId);
    }
  };

  const getReferenceFileSrc = useMemo(() => {
    if (!isValidObject(props.reference)) return null;

    const employeeId = manualApprovalData?.employee?.id;
    if (!employeeId) return null;

    return Object.values(props.reference).find(
      ({ employee }) => employee?.id === employeeId
    )?.fileSrc;
  }, [props.reference, manualApprovalData?.employee?.id]);

  const isReferencePictureAvailable = useMemo(() => {
    if (isValidObject(props.reference)) {
      const referencePicture = Object.values(props.reference).find(
        (data) => data?.employee?.id === props.data?.documentId
      );
      return isValidObject(referencePicture);
    } else {
      return false;
    }
    // eslint-disable-next-line
  }, [manualApprovalData, props.reference]);

  useEffect(() => {
    if (isValidObject(manualApprovalData)) {
      if (
        isReferencePictureAvailable === false ||
        props.forceReferencePictureUpdate === true
      ) {
        setUpdateReference(true);
      } else {
        setUpdateReference(false);
      }
    }
  }, [
    manualApprovalData,
    isReferencePictureAvailable,
    props.forceReferencePictureUpdate
  ]);

  const getReferencePicture = useMemo(() => {
    if (isValidObject(props.reference)) {
      return Object.values(props.reference).find(
        (data) => data?.employee?.id === props.data?.documentId
      )?.documentId;
    }
  }, [props.reference, props.data?.documentId]);

  const handleSaveProcedure = (procedureData, editingIndex) => {
    setFormData((prev) => {
      const updated = prev.linkedProcedures ? [...prev.linkedProcedures] : [];
      if (editingIndex === null) {
        updated.push(procedureData);
      } else {
        updated[editingIndex] = procedureData;
      }
      return { ...prev, linkedProcedures: updated };
    });
  };

  const handleDeleteProcedure = (procedureId) => {
    setFormData((prev) => {
      const updated = prev.linkedProcedures?.filter(
        (procedure) => procedure?.procedureId !== procedureId
      );
      return { ...prev, linkedProcedures: updated };
    });
  };

  useEffect(() => {
    const original = props.data || {};

    const hasBasic =
      !checkArraysAreEqual(formData.roles, original.roles || []) ||
      props.data.name !== formData.name ||
      props.data.phoneNumber !== formData.phoneNumber ||
      !!croppedImage;

    const hasLocation = !checkArraysAreEqual(
      formData.locations,
      original.connected?.location || []
    );

    const hasProcedure = !checkArraysAreEqual(
      formData.linkedProcedures || [],
      original.linkedProcedures || []
    );

    setHasChanges({
      basic: hasBasic,
      location: hasLocation,
      procedure: hasProcedure
    });
  }, [formData, croppedImage, props.data]);

  return (
    <>
      <PreviewBar
        title={
          <div className="display-flex flex-align-items-center">
            <div className="font-color-tertiaryColor font-size-larger">
              {props.data?.name}
            </div>
            <div
              style={{
                color: "var(--secondaryColor)"
              }}
              className="padding-left-default font-size-small text-uppercase"
            >
              {props.data?.type}
            </div>
          </div>
        }
        description={
          <div className=" display-flex flex-align-items-center">
            <div>{props.data?.employeeId}</div>
            <div
              style={{
                height: "6px",
                width: "6px",
                margin: "0 6px",
                borderRadius: "50%",
                background: "var(--tertiaryColor5)"
              }}
            />
            <div>{props.data?.email || props.data?.phoneNumber}</div>
          </div>
        }
        showNotify={false}
        onCloseClick={() => {
          if (step === "editing") {
            setStep("captures");
          } else {
            props.navigate("/workforce");
          }
        }}
        editOnClick={
          step !== "editing"
            ? () => {
                setStep("editing");
              }
            : null
        }
      >
        <div className="inherit-parent-height">
          {step === "selection" && (
            <div className="inherit-parent-height">
              <TypeSelection
                setFormData={setFormData}
                formData={formData}
                continueClick={() => {
                  setStep("edition");
                }}
              />
            </div>
          )}

          {step === "captures" && (
            <div className=" inherit-parent-height display-flex flex-justify-content-space-between flex-direction-column overflow-hidden">
              <div className="padding-bottom-large position-relative">
                <DateRange
                  // disabled={handleDisableFilter()}
                  loading={props.surveillanceLoading}
                  data-cy="DATE RANGE"
                  multiple={true}
                  data={capturesFilterData.dateRange}
                  onResetClick={(key) => {
                    props.setTableFilter("CAPTURES", defaultCapturesFilter);
                  }}
                  onApplyClick={() => {
                    props.setTableFilter("CAPTURES", selectedFilters);
                    setCurrentPage(1);
                  }}
                  applyButtonDisabled={
                    !(
                      selectedFilters?.["dateRange"]?.from !==
                        props.filterData?.dateRange?.from ||
                      selectedFilters?.["dateRange"]?.to !==
                        props.filterData?.dateRange?.to ||
                      selectedFilters?.["dateRange"]?.type !==
                        props.filterData?.dateRange?.type
                    )
                  }
                  setSelectedFilters={setSelectedFilters}
                  selectedFilters={selectedFilters}
                  appliedFilters={props.filterData}
                  selected={selectedFilters["dateRange"]}
                  width="100%"
                />
              </div>
              <div
                style={{
                  height: "100%"
                }}
                className="overflow-scroll"
              >
                {isValidArray(props.pendingCaptures) ? (
                  props.pictureApprovalLoading ? (
                    <CaptureSuspense length={props.pendingCaptures.length} />
                  ) : (
                    props.pendingCaptures.map((data, i) => (
                      <React.Fragment key={i}>
                        <ManualApprovalCard
                          data={data}
                          manualApprovalOnClick={() => {
                            setManualApprovalData(data);
                          }}
                        />
                      </React.Fragment>
                    ))
                  )
                ) : (
                  <></>
                )}

                <Captures
                  capturePictureData={props.capturePictureData.filter(
                    (captures) =>
                      !captures?.approval?.status ||
                      captures?.approval?.status === "APPROVED"
                  )}
                  surveillanceLoading={props.surveillanceLoading}
                  currentPage={currentPage}
                  pendingCaptures={props.pendingCaptures}
                />
              </div>

              {props.capturePictureData?.length > 1 ? (
                <Paginator
                  currentPage={currentPage}
                  totalPage={Math.ceil(props.capturePictureData?.length / 50)}
                  nextPageOnClick={() => {
                    setCurrentPage(currentPage + 1);
                  }}
                  previousPageOnClick={() => {
                    setCurrentPage(currentPage - 1);
                  }}
                />
              ) : (
                <></>
              )}
            </div>
          )}

          {step === "editing" && (
            <div className="inherit-parent-height display-flex flex-justify-content-space-between flex-direction-column overflow-hidden">
              <div
                style={{
                  height: "calc(100% - 56px)"
                }}
                className="overflow-scroll"
              >
                <div className="font-size-medium font-color-tertiaryColor">
                  BASIC INFORMATION
                </div>

                <BasicInfoForm
                  roles={props.roles}
                  validRoles={getValidRoles()}
                  formData={formData}
                  setFormData={setFormData}
                  setFile={setFile}
                  imageSrc={getImageSource()}
                  referencePictureData={props.referencePictureData}
                  loading={props.surveillanceLoading}
                  pictureApprovalLoading={props.pictureApprovalLoading}
                  isFaceDetected={isFaceDetected}
                  setIsFaceDetected={setIsFaceDetected}
                  type={croppedImage ? "add" : "view"}
                  setErrorStatus={props.setErrorStatus}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      [e.target.name]: e.target.value
                    });
                  }}
                  deleteReferencePictureOnClick={() => {
                    setShowDeletePictureConfirmationModal(
                      !showDeletePictureConfirmationModal
                    );
                  }}
                  isValidPhoneNumber={()=>{
                    return isValidNewPhoneNumber()
                  }}
                />
                {props.data?.type === "employee" ? (
                  <div className="inherit-parent-width padding-top-large">
                    <div className="">
                      <CustomSelect
                        data-cy="SELECT-LOCATION-FIELD"
                        search={true}
                        showSelectAll={true}
                        options={
                          isValidObject(props.locationData) &&
                          Object.values(props.locationData)
                            .sort((a, b) => {
                              const nameA = parseInt(a.PCTNo) || 0;
                              const nameB = parseInt(b.PCTNo) || 0;
                              return nameA - nameB;
                            })
                            .map((data) => ({
                              title: `${data.PCTNo} - ${data.location}`,
                              key: data.documentId
                            }))
                        }
                        placeholder="SELECT LOCATIONS"
                        handleSelectedData={(data) => {
                          const alreadySelected = formData?.locations?.includes(
                            data
                          );

                          setFormData((prev) => ({
                            ...prev,
                            locations: alreadySelected
                              ? prev.locations.filter((e) => e !== data)
                              : [...prev.locations, data]
                          }));
                        }}
                        selected={formData?.locations || []}
                        removeResponsive={true}
                        label={`LOCATIONS (${formData?.locations?.length})`}
                        value={
                          getPctNoUsingId(
                            formData?.locations,
                            props.locationData
                          ).join(", ") || ""
                        }
                        fontColor="font-color-tertiaryColor"
                        labelFontColor="font-color-tertiaryColor"
                        selectAll={() => {
                          const allLocationKeys =
                            isValidObject(props.locationData) &&
                            Object.values(props.locationData).map(
                              (d) => d.documentId
                            );

                          const isAllSelected =
                            formData?.locations?.length ===
                            allLocationKeys.length;

                          setFormData((prev) => ({
                            ...prev,
                            locations: isAllSelected ? [] : allLocationKeys
                          }));
                        }}
                      />
                    </div>
                    {
                      <ProcedureManagement
                        procedures={props.procedures}
                        linkedProcedures={formData.linkedProcedures}
                        onSaveProcedure={handleSaveProcedure}
                        onDeleteProcedure={handleDeleteProcedure}
                        isEditing={isEditing}
                        setIsEditing={setIsEditing}
                        roles={props.roles}
                        selectedRoles={formData.roles}
                      />
                    }
                  </div>
                ) : (
                  <></>
                )}
              </div>

              {!isEditing && isArchived && (
                <div
                  style={{ bottom: 0, zIndex: 1 }}
                  className="padding-top-larger display-flex inherit-parent-width"
                >
                  <Button
                    type="button"
                    variant="secondary"
                    text="Unarchive"
                    boxShadow={false}
                    loading={props.updateEmployeeLoading}
                    onClick={() => {
                      if (!props.claims?.employees?.update) {
                        props.setErrorStatus({
                          code: "custom",
                          message: "No permission to unarchive account"
                        });
                        return;
                      }
                      // employeeId renames leave the old doc archived as a
                      // backup; restoring it would duplicate the account
                      if (props.data?.actions?.backup === true) {
                        props.setErrorStatus({
                          code: "custom",
                          message:
                            "This is a backup of a renamed account and can't be unarchived"
                        });
                        return;
                      }
                      setShowUnarchiveConfirmationModal(true);
                    }}
                    data-cy="UNARCHIVE-EMPLOYEE-BUTTON"
                  />
                </div>
              )}

              {!isEditing && !isArchived && (
                <div
                  style={{ bottom: 0, zIndex: 1 }}
                  className="padding-top-larger display-flex inherit-parent-width"
                >
                  <div style={{ width: "70%" }}>
                    <Button
                      type="button"
                      disabled={handleDisable}
                      text={
                        step === "editing"
                          ? handleDisable
                            ? "Changes saved"
                            : "Save changes"
                          : "Continue"
                      }
                      loading={props.updateEmployeeLoading}
                      onClick={() => {
                        handleUploadEmployeeData();
                      }}
                      data-cy="submit-update-employee-form-button"
                    />
                  </div>
                  <div className="margin-left-larger" style={{ width: "30%" }}>
                    <Button
                      type="button"
                      variant="danger"
                      text="Archive"
                      boxShadow={false}
                      onClick={() => {
                        if (!props.claims?.employees?.update) {
                          props.setErrorStatus({
                            code: "custom",
                            message: "No permission to archive account"
                          });
                          return;
                        }
                        setShowArchiveConfirmationModal(true);
                      }}
                      data-cy="ARCHIVE-EMPLOYEE-BUTTON"
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </PreviewBar>

      <Modal
        show={file?.image?.name ? true : false}
        canIgnore={true}
        onClose={() => {
          setFile({ image: null });
        }}
        width="width-60-percentage"
        maxWidth="max-width-800px"
        maxHeight="max-height-80-percentage"
        background="false"
        boxShadow="false"
        borderRadius="false"
        height="height-80-percentage"
      >
        <div
          data-cy="cropper-modal"
          className={`font-family-gilroy-regular font-color-tertiaryColor6 background-color-tertiaryColor1`}
        >
          <CropperModal
            className="border-radius-default"
            OnBlobCreate={(blob) => {
              const imageFile = new File([blob], `${+new Date()}.png`, {
                type: "image/png"
              });
              setCroppedImage(imageFile);
            }}
            file={file?.image?.name ? URL.createObjectURL(file.image) : null}
            setFile={() => {
              setFile({ image: null });
            }}
          />
        </div>
      </Modal>
      <Modal
        show={isValidObject(manualApprovalData) ? true : false}
        canIgnore={true}
        onClose={() => {
          setManualApprovalData(null);
        }}
        width="width-30-percentage"
        maxHeight="max-height-80-percentage"
        background="false"
        boxShadow="false"
        borderRadius="false"
        height="height-50-percentage"
      >
        <div
          data-cy="MANUAL-APPROVAL-MODAL"
          className={`font-family-gilroy-regular font-color-tertiaryColor6  padding-larger background-color-tertiaryColor1 display-flex flex-justify-content-center flex-direction-column flex-align-items-center`}
        >
          <div>Review & approve capture</div>
          <div className="font-size-small padding-top-default">
            Review this member’s reference image against their latest capture.
          </div>

          <div className="padding-larger display-flex">
            <div>
              {manualApprovalData?.fileSrc ? (
                <img
                  src={manualApprovalData?.fileSrc}
                  alt={"new reference"}
                  height="100px"
                  width="100px"
                  onClick={() => openInNewTab(manualApprovalData.fileSrc)}
                  className="cursor-pointer"
                />
              ) : (
                <div
                  className="shimmer-dark"
                  style={{
                    height: "100px",
                    width: "100px",
                    backgroundColor: "var(--tertiaryColor3)"
                  }}
                />
              )}
              <div
                style={{
                  backgroundColor: "var(--tertiaryColor3)"
                }}
                className="display-flex flex-justify-content-center"
              >
                CAPTURE
              </div>
            </div>
            <div className="padding-large font-size-medium">
              <div className="display-flex flex-justify-content-center flex-align-items-center">
                Check if this person in the image is{" "}
                {manualApprovalData?.employee.name}
              </div>
              <div className="right-arrow-line" />
            </div>
            <div>
              {getReferenceFileSrc ? (
                <img
                  src={getReferenceFileSrc}
                  alt={"new reference"}
                  height="100px"
                  width="100px"
                  onClick={() => openInNewTab(manualApprovalData.fileSrc)}
                  className="cursor-pointer"
                />
              ) : isReferencePictureAvailable ? (
                <div
                  className="shimmer-dark"
                  style={{
                    height: "100px",
                    width: "100px",
                    backgroundColor: "var(--tertiaryColor3)"
                  }}
                />
              ) : (
                <div
                  className="padding-default flex-justify-content-center flex-align-items-center font-color-tertiaryColor6"
                  style={{
                    height: "100px",
                    width: "100px",
                    backgroundColor: "var(--tertiaryColor3)",
                    textAlign: "center"
                  }}
                >
                  N/A
                </div>
              )}
              <div
                style={{
                  backgroundColor: "var(--tertiaryColor3)"
                }}
                className="display-flex flex-justify-content-center"
              >
                REFERENCE
              </div>
            </div>
          </div>

          <div className="inherit-parent-width padding-large">
            <CaptureItem
              fields={[
                {
                  key: "CAPTURED AT",
                  value: `${dateAndTimeConverter(
                    manualApprovalData?.createdAt,
                    "Time"
                  )}, ${dateAndTimeConverter(
                    manualApprovalData?.createdAt,
                    "cardDate"
                  ) || "N/A"}`
                },
                {
                  key: "CAPTURED BY",
                  value: `${manualApprovalData?.capturedBy?.name ||
                    "Surveillance"}, ${alignPhoneNumber(
                    manualApprovalData?.capturedBy?.phoneNumber
                  ) || ""}`
                },
                {
                  key: "LOCATION",
                  value: `${manualApprovalData?.location?.PCTNo ||
                    "N/A"} - ${manualApprovalData?.location?.address || "N/A"}`
                }
              ]}
              showPicture={false}
            />
            {manualApprovalData?.approval?.geoContext?.currentLocation &&
              manualApprovalData?.approval?.geoContext?.selectedLocation && (
                <LocationMisMatch
                  onRedirectClick={() => {
                    const currentLocation =
                      manualApprovalData.approval.geoContext.currentLocation;
                    const selectedLocation =
                      manualApprovalData.approval.geoContext.selectedLocation;
                    openInNewTab(
                      `https://www.google.com/maps?q=${currentLocation.lat},${currentLocation.lng}+to+${selectedLocation.lat},${selectedLocation.lng}`
                    );
                  }}
                />
              )}
          </div>

          <div className="display-flex display-flex flex-align-items-center flex-justify-content-center">
            <div
              style={{
                opacity: isReferencePictureAvailable ? 1 : 0.5,
                width: "16px",
                height: "16px"
              }}
              data-cy="UPDATE-REFERENCE-CHECKBOX"
              onClick={() => {
                if (isReferencePictureAvailable === true) {
                  setUpdateReference((perv) => !perv);
                }
              }}
            >
              {updateReference ? (
                <CheckBoxActiveIcon color="var(--secondaryColor)" />
              ) : (
                <CheckBoxInActiveIcon color="var(--tertiaryColor6)" />
              )}
            </div>
            <div className="padding-left-default">
              Update face reference with the approved capture?
            </div>
          </div>

          <div
            className={`display-flex flex-justify-content-space-between padding-top-larger`}
          >
            <div
              style={{
                width: "60%"
              }}
              className="padding-right-large"
            >
              <Button
                data-cy="APPROVE-REFERENCE-BUTTON"
                text="Approve reference"
                boxShadow={false}
                variant="filter"
                className="inherit-parent-width padding-left-large padding-right-large"
                onClick={() => {
                  if (!props.claims?.surveillance?.update) {
                    props.setErrorStatus({
                      code: "custom",
                      message: "No permission to approve capture"
                    });
                    return;
                  }
                  props.approveCapturePicture({
                    approved: true,
                    updateReference: updateReference,
                    capturedData: manualApprovalData,
                    ...(getReferencePicture
                      ? {
                          reference: [
                            {
                              pictureId: getReferencePicture,
                              cosineDistance: 1
                            }
                          ]
                        }
                      : {})
                  });
                  setManualApprovalData(null);
                }}
              />
            </div>
            <div
              style={{
                width: "60%"
              }}
              className="padding-left-large"
            >
              <Button
                data-cy="REJECT-REFERENCE-BUTTON"
                variant="danger"
                text="Reject"
                boxShadow={false}
                className="inherit-parent-width padding-left-large padding-right-large"
                onClick={() => {
                  if (!props.claims?.surveillance?.update) {
                    props.setErrorStatus({
                      code: "custom",
                      message: "No permission to reject capture"
                    });
                    return;
                  }

                  props.approveCapturePicture({
                    approved: false,
                    capturedData: manualApprovalData,
                    updateReference: false
                  });
                  setManualApprovalData(null);
                }}
              />
            </div>
          </div>
        </div>
      </Modal>

      {/* Delete reference picture confirmation modal */}
      <Modal
        show={showDeletePictureConfirmationModal}
        canIgnore={true}
        onClose={() =>
          setShowDeletePictureConfirmationModal(
            !showDeletePictureConfirmationModal
          )
        }
        width=" width-400px"
        minWidth="width-400px"
        // maxHeight="max-height-90-percentage"
        background="background-color-tertiaryColor2"
        boxShadow="true"
        borderRadius="false"
        height="height-fit-content"
      >
        <div
          className="padding-larger display-flex flex-direction-column flex-align-items-center flex-justify-content-center text-align-center"
          style={{ color: "var(--tertiaryColor)" }}
          data-cy="excel-upload-modal"
        >
          <div className=" font-size-larger font-family-RHD-medium">
            Are you sure you want to delete the reference image?
          </div>
          <div className=" font-size-medium padding-top-default">
            This action is permanent and cannot be undone.
          </div>
          <div className=" padding-top-larger display-flex inherit-parent-width ">
            <Button
              type="button"
              data-cy={`YES-DELETE-BUTTON`}
              text="Yes, Delete"
              boxShadow={false}
              variant="danger"
              // loading={props.complaintsLoading.import}
              className=" padding-left-larger padding-right-larger font-color-danger "
              onClick={() => {
                props.deleteReferencePicture(
                  props.referencePictureData?.documentId
                );
                setShowDeletePictureConfirmationModal(
                  !showDeletePictureConfirmationModal
                );
              }}
            />

            <Button
              data-cy={`CANCEL-DELETE-BUTTON`}
              text="Cancel"
              boxShadow={false}
              variant="transparent"
              // loading={props.complaintsLoading.import}
              className=" padding-left-larger padding-right-larger"
              onClick={() => {
                setShowDeletePictureConfirmationModal(
                  !showDeletePictureConfirmationModal
                );
              }}
            />
          </div>
        </div>
      </Modal>

      {/* Archive account confirmation modal */}
      <Modal
        show={showArchiveConfirmationModal}
        canIgnore={true}
        onClose={() => setShowArchiveConfirmationModal(false)}
        width=" width-400px"
        minWidth="width-400px"
        background="background-color-tertiaryColor2"
        boxShadow="true"
        borderRadius="false"
        height="height-fit-content"
      >
        <div
          className="padding-larger display-flex flex-direction-column flex-align-items-center flex-justify-content-center text-align-center"
          style={{ color: "var(--tertiaryColor)" }}
          data-cy="archive-employee-modal"
        >
          <div className=" font-size-larger font-family-RHD-medium">
            Are you sure you want to archive this account?
          </div>
          <div className=" font-size-medium padding-top-default">
            {props.data?.name || "This account"} will lose access and be
            removed from all locations and procedures.
          </div>
          <div className=" padding-top-larger display-flex inherit-parent-width ">
            <Button
              type="button"
              data-cy={`YES-ARCHIVE-BUTTON`}
              text="Yes, Archive"
              boxShadow={false}
              variant="danger"
              className=" padding-left-larger padding-right-larger font-color-danger "
              onClick={() => {
                props.archiveEmployee(props.data?.documentId);
                setShowArchiveConfirmationModal(false);
                props.navigate("/workforce");
              }}
            />

            <Button
              data-cy={`CANCEL-ARCHIVE-BUTTON`}
              text="Cancel"
              boxShadow={false}
              variant="transparent"
              className=" padding-left-larger padding-right-larger"
              onClick={() => setShowArchiveConfirmationModal(false)}
            />
          </div>
        </div>
      </Modal>

      {/* Unarchive account confirmation modal */}
      <Modal
        show={showUnarchiveConfirmationModal}
        canIgnore={true}
        onClose={() => setShowUnarchiveConfirmationModal(false)}
        width=" width-400px"
        minWidth="width-400px"
        background="background-color-tertiaryColor2"
        boxShadow="true"
        borderRadius="false"
        height="height-fit-content"
      >
        <div
          className="padding-larger display-flex flex-direction-column flex-align-items-center flex-justify-content-center text-align-center"
          style={{ color: "var(--tertiaryColor)" }}
          data-cy="unarchive-employee-modal"
        >
          <div className=" font-size-larger font-family-RHD-medium">
            Are you sure you want to unarchive this account?
          </div>
          <div className=" font-size-medium padding-top-default">
            {props.data?.name || "This account"} will be restored without
            roles, locations or procedures. Assign them again to give access.
          </div>
          <div className=" padding-top-larger display-flex inherit-parent-width ">
            <Button
              type="button"
              data-cy={`YES-UNARCHIVE-BUTTON`}
              text="Yes, Unarchive"
              boxShadow={false}
              variant="secondary"
              className=" padding-left-larger padding-right-larger"
              onClick={() => {
                props.unarchiveEmployee(props.data?.documentId);
                setShowUnarchiveConfirmationModal(false);
                props.navigate("/workforce");
              }}
            />

            <Button
              data-cy={`CANCEL-UNARCHIVE-BUTTON`}
              text="Cancel"
              boxShadow={false}
              variant="transparent"
              className=" padding-left-larger padding-right-larger"
              onClick={() => setShowUnarchiveConfirmationModal(false)}
            />
          </div>
        </div>
      </Modal>
    </>
  );
}
export default WorkforcePreview;

const TypeSelection = (props) => {
  const data = [
    {
      title: "Employee",
      description: "Field operatives for execution of objectives"
    },
    {
      title: "Admin",
      description: "Executive and Management Personnel to oversee operations"
    }
  ];

  return (
    <div className="display-flex flex-direction-column flex-justify-content-space-between inherit-parent-height">
      <div>
        <div className="padding-bottom-large">TYPE</div>
        {data?.map((data, i) => (
          <div
            key={i}
            onClick={() => {
              props.setFormData({
                ...props.formData,
                type: data?.title.toLocaleLowerCase()
              });
            }}
            className="padding-bottom-large"
          >
            <SelectionRadioCard
              data={data}
              selected={
                props.formData?.type === data?.title.toLocaleLowerCase()
              }
            />
          </div>
        ))}
      </div>
      <div>
        <Button
          type="button"
          data-cy="TYPE-CONFIRM-BUTTON"
          disabled={props.formData?.type ? false : true}
          text="Continue"
          onClick={props.continueClick}
        />
      </div>
    </div>
  );
};

const BasicInfoForm = (props) => {
  const getOptions = useMemo(() => {
    if (!isValidObject(props.roles)) {
      return;
    }
    const hasValidRole = props.formData?.roles.some((role) =>
      props.validRoles.includes(role)
    );

    const allRoles = Object.values(props.roles).filter(
      (data) => data?.type === props.formData?.type
    );

    const selectedRoles = allRoles.filter((data) =>
      props.formData?.roles.includes(data?.documentId)
    );

    const filteredRoles = [
      ...new Map(
        [
          ...allRoles.filter(
            (data) => !hasValidRole || !isValidObject(data?.attendance?.capture)
          ),
          ...selectedRoles
        ].map((item) => [item.documentId, item])
      ).values()
    ];

    const filteredKeys = new Set(filteredRoles.map((data) => data?.documentId));

    const options = allRoles.map((data) => ({
      title: data?.name,
      key: data?.documentId,
      disabled: !filteredKeys.has(data?.documentId)
    }));

    return options;
  }, [
    props.formData?.roles,
    props.validRoles,
    props.roles,
    props.formData?.type
  ]);

  return (
    <>
      <form onChange={props.onChange} className="padding-top-large">
        <div className="padding-bottom-default font-size-smaller font-color-tertiaryColor">
          PROFILE
        </div>
        <ProfilePhoto
          referencePicture={props.referencePictureData?.fileSrc}
          name={props.formData?.name}
          loading={props.loading}
          approveReferenceOnClick={(bool) => {
            props.approveReferenceOnClick(bool);
          }}
          deleteReferencePictureOnClick={props.deleteReferencePictureOnClick}
          pictureApprovalLoading={props.pictureApprovalLoading}
        />
        {props.formData?.type === "employee" && (
          <InputBox
            className="inherit-parent-width margin-top-large"
            name="name"
            label="NAME*"
            type="text"
            placeholder={`Enter ${capitalizeFirstLetter(
              props.formData?.type
            )} Name`}
            // disabled={true}
            removeResponsive={true}
            value={props.formData?.name}
            fontColor="font-color-tertiaryColor"
            labelFontColor="font-color-tertiaryColor"
            data-cy="NAME-FIELD"
            disabledBackgroundColor="transparent"
            required
          />
        )}
        {props.formData?.type === "employee" && (
          <InputBox
            className="inherit-parent-width"
            name="phoneNumber"
            label="PHONE NUMBER*"
            // disabled={true}
            type="text"
            placeholder={`Enter ${capitalizeFirstLetter(
              props.formData?.type
            )} Phone Number`}
            removeResponsive={true}
            value={props.formData?.phoneNumber}
            fontColor="font-color-tertiaryColor"
            disabledBackgroundColor="transparent"
            labelFontColor="font-color-tertiaryColor"
            data-cy="PHONE-NUMBER-FIELD"
            required
            validation = {()=> {
              return props.isValidPhoneNumber()
            }}
          />
        )}
        {/* {props.formData?.type === "admin" && props.formData?.email && (
          <InputBox
            className="inherit-parent-width"
            name="email"
            label="EMAIL*"
            type="text"
            placeholder={`Enter ${capitalizeFirstLetter(
              props.formData?.type
            )} Email`}
            disabled={true}
            disabledBackgroundColor="transparent"
            removeResponsive={true}
            value={props.formData?.email}
            fontColor="font-color-tertiaryColor"
            labelFontColor="font-color-tertiaryColor"
            data-cy="EMAIL-FIELD"
            required
          />
        )} */}
      </form>

      {isValidObject(props.roles) && (
        <div className="padding-top-large inherit-parent-width">
          <CustomSelect
            options={getOptions}
            search={true}
            placeholder="SELECT ONE OR MORE ROLES"
            handleSelectedData={(data) => {
              if (props.formData?.roles.includes(data)) {
                props.setFormData({
                  ...props.formData,
                  roles: props.formData?.roles.filter((e) => e !== data)
                });
              } else {
                props.setFormData({
                  ...props.formData,
                  roles: [...props.formData?.roles, data]
                });
              }
            }}
            selected={props.formData?.roles}
            removeResponsive={true}
            label={`ROLES (${props.formData?.roles.length})`}
            value={
              getRolesNameUsingId(props.formData?.roles, props.roles).join(
                ", "
              ) || ""
            }
            fontColor="font-color-tertiaryColor"
            labelFontColor="font-color-tertiaryColor"
          />
        </div>
      )}
    </>
  );
};

function Captures({
  capturePictureData,
  surveillanceLoading,
  currentPage,
  pendingCaptures
}) {
  if (surveillanceLoading) {
    return <CaptureSuspense />;
  }

  return isValidArray(capturePictureData) ? (
    capturePictureData
      .slice(currentPage * 50 - 50, currentPage * 50)
      .map((data, index) => (
        <React.Fragment key={index}>
          <CaptureItem
            fileSrc={data?.fileSrc}
            fields={[
              {
                key: "CAPTURED AT",
                value: data?.createdAt
                  ? `${dateAndTimeConverter(
                      data.createdAt,
                      "Time"
                    )}, ${dateAndTimeConverter(data.createdAt, "cardDate") ||
                      "N/A"}`
                  : "N/A"
              },
              {
                key: "CAPTURED BY",
                value: `${
                  data?.capturedBy?.name?.length > 15
                    ? data.capturedBy.name.slice(0, 15) + "..."
                    : data?.capturedBy?.name || "Surveillance"
                }${
                  data?.capturedBy?.phoneNumber
                    ? `, ${alignPhoneNumber(data.capturedBy.phoneNumber)}`
                    : ""
                }`
              },
              {
                key: "LOCATION",
                value: `${data?.location?.PCTNo || "N/A"}, ${
                  data?.location?.address?.length > 15
                    ? data.location.address.slice(0, 15) + "..."
                    : data?.location?.address || "N/A"
                }`
              }
            ]}
          />
        </React.Fragment>
      ))
  ) : !isValidArray(pendingCaptures) ? (
    <div className="display-flex font-color-tertiaryColor inherit-parent-height inherit-parent-width flex-justify-content-center flex-align-items-center">
      NO CAPTURE DATA FOUND
    </div>
  ) : (
    <></>
  );
}

const CaptureSuspense = (props) => (
  <section data-cy="capture-suspense">
    {[...Array(props.length || 10)].map((_, index) => (
      <div
        key={index}
        className="margin-top-large display-flex margin-bottom-larger inherit-parent-width"
        style={{ height: "60px" }}
      >
        <div>
          <div
            style={{ width: "60px", height: "60px" }}
            className="shimmer-dark padding-large"
          />
        </div>
        <div className="inherit-parent-width padding-left-large">
          <div
            style={{ width: "10%", height: "15px" }}
            className="shimmer-dark padding-default margin-bottom-small"
          />
          <div
            style={{ width: "40%", height: "15px" }}
            className="shimmer-dark padding-default margin-bottom-small"
          />
          <div
            style={{ width: "20%", height: "15px" }}
            className="shimmer-dark padding-default margin-bottom-small"
          />
        </div>
      </div>
    ))}
  </section>
);

const getRolesNameUsingId = (selectedData, allData) => {
  let updatedData = [];
  selectedData.forEach((element) => {
    updatedData.push(allData[element]?.name);
  });
  return updatedData;
};

const Paginator = (props) => {
  return (
    <div className="display-flex inherit-parent-width flex-justify-content-center padding-top-large">
      <div>
        <div
          className={`${
            props.currentPage <= props.totalPage && props.currentPage > 1
              ? "cursor-pointer"
              : "cursor-no-drop"
          }`}
          onClick={() => {
            if (props.currentPage <= props.totalPage && props.currentPage > 1) {
              props.previousPageOnClick();
            }
          }}
          style={{
            transform: "rotate(90deg)",
            height: "16px",
            width: "16px"
          }}
        >
          <ArrowIcon
            color={
              props.currentPage <= props.totalPage && props.currentPage > 1
                ? "var(--tertiaryColor6)"
                : "var(--tertiaryColor5)"
            }
          />
        </div>
      </div>

      <div className="display-flex font-size-small padding-left-larger padding-right-larger">
        <div className="padding-right-default font-color-tertiaryColor6">
          PAGE
        </div>
        <div className="font-color-tertiaryColor">
          {props.currentPage} of {props.totalPage}
        </div>
      </div>

      <div>
        <div
          className={`${
            props.currentPage < props.totalPage
              ? "cursor-pointer"
              : "cursor-no-drop"
          }`}
          onClick={() => {
            if (props.currentPage < props.totalPage) {
              props.nextPageOnClick();
            }
          }}
          style={{
            transform: "rotate(-90deg)",
            height: "16px",
            width: "16px"
          }}
        >
          <ArrowIcon
            color={
              props.currentPage < props.totalPage
                ? "var(--tertiaryColor6)"
                : "var(--tertiaryColor5)"
            }
          />
        </div>
      </div>
    </div>
  );
};

const ProfilePhoto = (props) => {
  const [hovered, setHovered] = useState(false);

  return (
    <div>
      {props.referencePicture ? (
        <></>
      ) : (
        <div
          data-cy="INITIAL-FALLBACK"
          className="font-color-tertiaryColor6"
          style={{
            ...props.style,
            backgroundColor: "rgb(54, 54, 54)",
            background:
              "linear-gradient(180deg, rgba(0, 0, 0, 0) 20%, #000000 100%)",
            width: "80px",
            height: "80px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "30px",
            fontWeight: "500",
            position: "relative"
          }}
        >
          {getInitials(props.name)}
        </div>
      )}

      {props.referencePicture ? (
        <div
          style={{
            position: "relative",
            width: "80px",
            height: "80px",
            overflow: "hidden"
          }}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          <img
            data-cy="REFERENCE-PHOTO"
            src={props.referencePicture}
            alt="reference"
            style={{
              height: "100%",
              width: "100%",
              objectFit: "cover",
              display: "block"
            }}
          />
          {hovered && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                height: "100%",
                width: "100%",
                background:
                  "linear-gradient(180deg, rgba(0, 0, 0, 0) 20%, #000000 100%)"
              }}
              className=" display-flex flex-justify-content-center flex-align-items-end padding-bottom-small"
            >
              <div
                className=" cursor-pointer"
                onClick={() => {
                  if (!props.loading) {
                    props.deleteReferencePictureOnClick();
                  }
                }}
              >
                <DeleteIcon width="20" height="20" />
              </div>
            </div>
          )}
        </div>
      ) : (
        <></>
      )}

      {props.newReferencePicture ? (
        <div>
          <div className=" display-flex flex-justify-content-space-between flex-align-items-center">
            <div>
              {props.referencePicture ? (
                <img
                  src={props.referencePicture}
                  alt="reference"
                  height="80px"
                  width="80px"
                />
              ) : (
                <div
                  className=" font-size-medium text-align-center display-flex flex-justify-content-center flex-align-items-center"
                  style={{
                    width: "80px",
                    height: "80px",
                    backgroundColor: "var(--tertiaryColor1)",
                    color: "var(--tertiaryColor5)"
                  }}
                >
                  New member
                </div>
              )}
              <div
                className=" font-color-tertiaryColor6 display-flex flex-justify-content-center flex-align-items-center font-size-small"
                style={{
                  backgroundColor: "var(--tertiaryColor3)",
                  width: "80px",
                  height: "20px",
                  position: "relative",
                  top: "-5px"
                }}
              >
                OLD
              </div>
            </div>

            <div className=" padding-horizontal-larger">
              <div
                className=" font-size-medium"
                style={{ color: "var(--tertiaryColor6)" }}
              >
                Please check if this person in the image is{" "}
                <span style={{ color: "var(--tertiaryColor)" }}>
                  {props.name}
                </span>
              </div>
              <div className=" display-flex flex-justify-content-space-between flex-align-items-center">
                <div
                  style={{
                    backgroundColor: "var(--tertiaryColor5)",
                    height: "2px",
                    width: "100%"
                  }}
                />
                <div
                  style={{
                    transform: "rotate(-90deg)",
                    height: "16px",
                    width: "16px",
                    position: "relative",
                    left: "-9px"
                  }}
                >
                  <ArrowIcon color={"var(--tertiaryColor5)"} />
                </div>
              </div>
            </div>

            <div>
              <img
                src={props.newReferencePicture}
                alt="reference"
                height="80px"
                width="80px"
              />
              <div
                className=" font-color-tertiaryColor6 display-flex flex-justify-content-center flex-align-items-center font-size-small font-family-RHD-medium"
                style={{
                  backgroundColor: "var(--tertiaryColor3)",
                  width: "80px",
                  height: "20px",
                  position: "relative",
                  top: "-5px"
                }}
              >
                NEW
              </div>
            </div>
          </div>

          <div className="padding-top-large">
            <Button
              type="button"
              text="Approve reference"
              loading={props.loading}
              boxShadow={false}
              onClick={() => {
                props.approveReferenceOnClick(true);
              }}
              data-cy="approve-reference-photo-button"
            />

            <Button
              variant="danger"
              type="button"
              text="Reject"
              loading={props.loading}
              boxShadow={false}
              className=" margin-top-medium"
              onClick={() => {
                props.approveReferenceOnClick(false);
              }}
              data-cy="reject-reference-photo-button"
            />
          </div>
        </div>
      ) : (
        <></>
      )}
    </div>
  );
};

export const Fields = (props) => {
  return (
    <div
      style={{
        width: "100%",
        fontSize: "13px"
      }}
      className="display-flex padding-top-small flex-justify-content-space-between"
    >
      <div className="display-flex">
        <div
          className="white-space-no-wrap font-color-tertiaryColor6"
          style={{ width: "180px" }}
          data-cy={`${props.title}`}
        >
          {props.title}
        </div>
      </div>
      <div
        className={`text-align-right inherit-parent-width text-overflow-ellipsis font-color-tertiaryColor ${!props.removeCapitalize &&
          "text-capitalize"}`}
        data-cy={props.dataCy || props.data}
        style={{
          minWidth: "fit-content",
          maxWidth: "380px"
        }}
        title={props.data}
      >
        {props.data}
      </div>
    </div>
  );
};

const ManualApprovalCard = (props) => {
  return (
    <div
      data-cy={`${props.data.documentId}-MANUAL-APPROVAL-CARD`}
      className=" display-flex flex-justify-content-space-between flex-align-items-center padding-bottom-large"
      style={{ width: "calc(100% - 5px)" }}
      onClick={props.manualApprovalOnClick}
    >
      <div className="inherit-parent-width display-flex">
        {props.data?.fileSrc ? (
          <img
            src={props.data?.fileSrc}
            alt={"new reference"}
            height="60px"
            width="60px"
            onClick={() => openInNewTab(props.data?.fileSrc)}
            className=" cursor-pointer"
          />
        ) : (
          <div
            className="shimmer-dark"
            style={{
              height: "60px",
              width: "60px",
              backgroundColor: "var(--tertiaryColor3)"
            }}
          />
        )}
        <div className=" padding-left-large">
          <div className=" font-size-medium display-flex flex-align-items-center ">
            <div className=" font-color-tertiaryColor6">
              {dateAndTimeConverter(props.data?.createdAt, "thirdDate")},{" "}
              {dateAndTimeConverter(props.data?.createdAt, "Time")}
            </div>
            <div
              style={{
                backgroundColor: "var(--tertiaryColor5)",
                width: "6px",
                height: "6px",
                borderRadius: "8px",
                margin: "0px 8px"
              }}
            />
            <div className=" font-size-small" style={{ color: "#D17541" }}>
              PENDING APPROVAL
            </div>
          </div>
          <div className=" font-color-tertiaryColor6 font-size-small padding-top-default white-space-no-wrap text-overflow-ellipsis">
            CAPTURED BY{" "}
            <span className=" font-color-tertiaryColor">
              {props.data?.capturedBy?.name || "N/A"},
              {props.data?.capturedBy?.phoneNumber || "N/A"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const LocationMisMatch = (props) => {
  return (
    <div
      onClick={props.onRedirectClick}
      style={{
        backgroundColor: "#D1754114"
      }}
      className="inherit-parent-width padding-large display-flex flex-justify-content-space-between cursor-pointer"
    >
      <div className="display-flex flex-align-items-center flex-justify-content-center">
        <div
          style={{
            width: "20px",
            height: "20px"
          }}
        >
          <NotInLocationIcon />
        </div>
        <div className="padding-left-default">
          Location mismatch. Review here
        </div>
      </div>
      <div
        style={{
          width: "20px",
          height: "20px"
        }}
      >
        <RedirectIcon height="15" width="15" />
      </div>
    </div>
  );
};

const getPctNoUsingId = (selectedData = [], allData = {}) => {
  return selectedData
    .map((id) => allData[id]?.PCTNo)
    .filter((pctNo) => pctNo !== undefined)
    .sort((a, b) => a - b);
};

const ProcedureManagement = ({
  procedures = {},
  linkedProcedures = [],
  onSaveProcedure,
  onDeleteProcedure,
  isEditing,
  setIsEditing,
  roles,
  selectedRoles
}) => {
  const [editingIndex, setEditingIndex] = useState(null);
  const [procedureForm, setProcedureForm] = useState({
    procedureId: "",
    interval: "",
    period: "",
    from: "",
    to: ""
  });

  const getHourLabel = (value) =>
    Object.keys(hours).find((key) => hours[key] === value) || value;

  const handleCreateNewIconClick = () => {
    setProcedureForm({
      procedureId: "",
      interval: "",
      period: "",
      from: "",
      to: ""
    });
    setIsEditing(true);
    setEditingIndex(null);
  };

  useEffect(() => {
    if (editingIndex !== null && isEditing) {
      const procedure = linkedProcedures[editingIndex];
      setProcedureForm({
        procedureId: procedure?.procedureId,
        interval: procedure?.interval ? String(procedure.interval) : "",
        period: procedure?.period
          ? capitalizeFirstLetter(procedure.period)
          : "",
        from: getHourLabel(procedure?.from),
        to: getHourLabel(procedure?.to)
      });
    }
  }, [editingIndex, isEditing, linkedProcedures]);

  const handleEdit = (index) => {
    setIsEditing(true);
    setEditingIndex(index);
  };

  const handleSave = () => {
    if (!procedureForm.procedureId || !procedureForm.from || !procedureForm.to)
      return;

    const procedureData = {
      procedureId: procedureForm.procedureId,
      interval: procedureForm.interval || "",
      period: procedureForm.period ? procedureForm.period.toLowerCase() : "",
      from: hours[procedureForm.from] || procedureForm.from,
      to: hours[procedureForm.to] || procedureForm.to
    };

    onSaveProcedure(procedureData, editingIndex);
    handleCancel();
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditingIndex(null);
    setProcedureForm({
      procedureId: "",
      interval: "",
      period: "",
      from: "",
      to: ""
    });
  };

  const handleDelete = (procedureId) => {
    onDeleteProcedure(procedureId);
    setIsEditing(false);
    setEditingIndex(null);
    setProcedureForm({
      procedureId: "",
      interval: "",
      period: "",
      from: "",
      to: ""
    });
  };

  const handleFormChange = (field, value) => {
    setProcedureForm((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleProcedureChange = (procedureId) => {
    setProcedureForm((prev) => ({
      ...prev,
      procedureId
    }));
  };

  const EditModal = () => {
    const modalRef = useRef(null);

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (modalRef.current && !modalRef.current.contains(event.target)) {
          handleCancel();
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [modalRef]);

    const getProcedureOptions = () => {
      const isIndependentEngineer = selectedRoles?.some(
        (roleId) =>
          roles[roleId]?.access?.moduleAccess?.independentEngineer === true
      );

      const selectedProc = Object.values(procedures).find(
        (proc) => proc.documentId === procedureForm.procedureId
      );

      let unlinkedProcedures = Object.values(procedures).filter(
        (proc) =>
          !linkedProcedures?.some(
            (linked) => linked.procedureId === proc.documentId
          )
      );

      unlinkedProcedures = unlinkedProcedures.filter((proc) =>
        isIndependentEngineer
          ? proc.procedureType === "independentEngineer"
          : proc.procedureType !== "independentEngineer"
      );

      if (
        selectedProc &&
        !unlinkedProcedures.some(
          (proc) => proc.documentId === selectedProc.documentId
        )
      ) {
        unlinkedProcedures.unshift(selectedProc);
      }

      return unlinkedProcedures.map((proc) => ({
        key: proc.documentId,
        title: proc.title
      }));
    };

    const getHourOptions = () => [
      ...Object.keys(hours).map((hour) => ({
        key: hour,
        title: hour
      }))
    ];

    const isDisabled = useMemo(() => {
      if (
        !procedureForm.procedureId ||
        !procedureForm.from ||
        !procedureForm.to
      ) {
        return true;
      }

      if (hours[procedureForm.from] > hours[procedureForm.to]) {
        return true;
      }

      const original = {
        procedureId: linkedProcedures[editingIndex]?.procedureId,
        interval: linkedProcedures[editingIndex]?.interval
          ? String(linkedProcedures[editingIndex].interval)
          : "",
        period: linkedProcedures[editingIndex]?.period
          ? capitalizeFirstLetter(linkedProcedures[editingIndex].period)
          : "",
        from: getHourLabel(linkedProcedures[editingIndex]?.from),
        to: getHourLabel(linkedProcedures[editingIndex]?.to)
      };

      const current = {
        procedureId: procedureForm.procedureId,
        interval: procedureForm.interval,
        period: procedureForm.period,
        from: procedureForm.from,
        to: procedureForm.to
      };

      return JSON.stringify(original) === JSON.stringify(current);
      // eslint-disable-next-line
    }, [procedureForm, linkedProcedures, editingIndex]);

    return (
      <div
        ref={modalRef}
        style={{
          padding: "24px",
          backgroundColor:
            getComputedStyle(document.documentElement).getPropertyValue(
              "--primaryColor"
            ) === "#F3F3F3"
              ? "var(--tertiaryColor8)"
              : "var(--tertiaryColor1)"
        }}
      >
        <div className="padding-bottom-large">
          <CustomSelect
            search={true}
            optionHeight="250px"
            data-cy="PROCEDURE-FIELD"
            value={procedures?.[procedureForm.procedureId]?.title}
            label="PROCEDURE"
            options={getProcedureOptions()}
            selected={[procedureForm.procedureId]}
            handleSelectedData={(value) => handleProcedureChange(value)}
            className="text-transform-capitalize"
            fontColor="font-color-tertiaryColor"
          />
        </div>
        <div className="display-flex padding-bottom-large">
          <CustomSelect
            search={false}
            data-cy="INTERVAL-FIELD"
            optionHeight="200px"
            value={procedureForm.interval || "None"}
            label="ONCE EVERY"
            options={[
              { key: "", title: "None" },
              ...[...Array(24)].map((_, i) => ({
                key: (i + 1).toString(),
                title: (i + 1).toString()
              }))
            ]}
            selected={[procedureForm.interval]}
            handleSelectedData={(value) => handleFormChange("interval", value)}
            className="text-transform-capitalize"
            fontColor="font-color-tertiaryColor"
          />
          <div style={{ padding: "16px", height: "100%" }} />
          <CustomSelect
            search={false}
            data-cy="PERIOD-FIELD"
            value={procedureForm.period || "None"}
            label="PERIOD"
            options={[
              { key: "", title: "None" },
              { key: "Hours", title: "Hours" },
              { key: "Days", title: "Days" },
              { key: "Months", title: "Months" }
            ]}
            selected={[procedureForm.period]}
            handleSelectedData={(value) => handleFormChange("period", value)}
            className="text-transform-capitalize"
            fontColor="font-color-tertiaryColor"
          />
        </div>
        <div className="display-flex padding-bottom-default">
          <CustomSelect
            search={false}
            data-cy="FROM-FIELD"
            optionHeight="200px"
            value={procedureForm.from}
            label="FROM"
            options={getHourOptions()}
            selected={[procedureForm.from]}
            handleSelectedData={(value) => handleFormChange("from", value)}
            className="text-transform-capitalize"
            fontColor="font-color-tertiaryColor"
          />
          <div style={{ padding: "16px", height: "100%" }} />
          <CustomSelect
            search={false}
            data-cy="TO-FIELD"
            optionHeight="200px"
            value={procedureForm.to}
            label="TO"
            options={getHourOptions()}
            selected={[procedureForm.to]}
            handleSelectedData={(value) => handleFormChange("to", value)}
            className="text-transform-capitalize"
            fontColor="font-color-tertiaryColor"
          />
        </div>

        <div
          className="font-color-danger font-size-small padding-bottom-large"
          style={{ height: "24px" }}
          data-cy="VALIDATION-ERROR-MESSAGE"
        >
          {hours[procedureForm.from] > hours[procedureForm.to]
            ? "Invalid time range: 'start time' is greater than 'end time'"
            : ""}
        </div>

        <div className="display-flex inherit-parent-width">
          <div style={{ width: "70%" }}>
            <Button
              text={editingIndex !== null && isEditing ? "Update" : "Add"}
              onClick={handleSave}
              boxShadow={false}
              disabled={isDisabled}
              data-cy={`${
                editingIndex !== null && isEditing ? "UPDATE" : "ADD"
              }-PROCEDURE-BUTTON`}
            />
          </div>
          <div className="margin-left-larger" style={{ width: "30%" }}>
            <Button
              onClick={() => handleDelete(procedureForm.procedureId)}
              data-cy="DELETE-PROCEDURE"
              boxShadow={false}
              variant="danger"
              text="Delete"
            />
          </div>
        </div>
      </div>
    );
  };
  return (
    <div className="inherit-parent-width font-color-tertiaryColor">
      <div
        className="font-size-medium display-flex flex-justify-content-space-between "
        style={{ paddingBottom: "8px", paddingTop: "8px" }}
      >
        <div>PROCEDURE INFORMATION</div>
        {linkedProcedures.length > 0 && (
          <div
            onClick={handleCreateNewIconClick}
            className="cursor-pointer"
            data-cy="ASSIGN-PROCEDURES-ICON"
          >
            <AddIcon color="var(--tertiaryColor)" />
          </div>
        )}
      </div>

      {linkedProcedures.length === 0 && !isEditing && (
        <>
          <div
            onClick={handleCreateNewIconClick}
            className="display-flex font-color-tertiaryColor6 font-size-small flex-justify-content-space-between flex-align-items-center cursor-pointer text-capitalize"
            style={{
              border: "dashed 1.7px var(--tertiaryColor5)",
              padding: "12px"
            }}
            data-cy="ASSIGN-NEW-PROCEDURE"
          >
            <div>assign procedure</div>
            <AddIcon color="var(--tertiaryColor)" />
          </div>
          <div className="font-size-small font-color-tertiaryColor6 margin-top-default">
            You can add the active duration and frequency for each procedure.
          </div>
        </>
      )}

      <div>
        {linkedProcedures.map((procedure, index) => {
          if (isEditing && editingIndex === index) {
            return <EditModal key={index} />;
          }

          return (
            procedures[procedure.procedureId] && (
              <div
                key={index}
                className=" font-size-small  flex-justify-content-space-between display-flex flex-align-items-center"
                style={{ padding: "8px 0" }}
                data-cy={`${
                  procedures[procedure.procedureId]?.title
                }-PROCEDURE_CARD`}
              >
                <div className="display-flex">
                  <div>{procedures[procedure.procedureId]?.title}</div>
                  <div className="font-size-small font-color-secondaryColor margin-left-small display-flex flex-align-items-center">
                    {procedure.interval && procedure.period
                      ? `Once every ${procedure.interval} ${capitalizeFirstLetter(
                          procedure.period
                        ).slice(0, -1)} (s)`
                      : "On demand"}
                  </div>
                </div>

                <div
                  onClick={() => handleEdit(index)}
                  className="cursor-pointer"
                  data-cy="PROCEDURE-EDIT-ICON"
                >
                  <EditIcon color="var(--tertiaryColor6" />
                </div>
              </div>
            )
          );
        })}

        {isEditing && editingIndex === null && <EditModal />}
      </div>
    </div>
  );
};
